// @LTG-ID: F-008
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-008, source Landing Page.html lines 2081-2130
// @CHANGED: 2026-02 session_002
// @OWNER: web

import { useState } from "react";
import { REVENUE_TYPES } from "../../data/revenueTypes";
import "./dollar-of-revenue.css";

function sectorMultipleLabel(multiplier) {
    if (multiplier >= 3) return "7.0x to 9.0x";
    if (multiplier >= 2) return "4.5x to 6.0x";
    if (multiplier >= 1) return "2.5x to 4.0x";
    return "1.0x to 2.5x";
}

function riskCopy(multiplier) {
    if (multiplier >= 3) return "reduces";
    if (multiplier >= 2) return "partly reduces";
    if (multiplier >= 1) return "does not reduce";
    return "increases";
}

export function DollarOfRevenue() {
    const [active, setActive] = useState("recurring");
    const cur = REVENUE_TYPES.find((t) => t.key === active) || REVENUE_TYPES[0];

    return (
        <section
            className="ltg-block ltg-dor-block"
            id="dollar-of-revenue"
            data-testid="dollar-of-revenue"
        >
            <div className="ltg-narrow">
                <div className="ltg-block-head">
                    <span className="ltg-eyebrow">
                        The math owners are surprised by
                    </span>
                    <h2>
                        The same dollar of revenue is worth{" "}
                        <em>very different</em> dollars of enterprise value
                        <em>.</em>
                    </h2>
                </div>
                <p className="ltg-dor__lede">
                    Most owners assume their revenue is fungible. It is not.
                    Buyers price the same dollar very differently depending on
                    how it arrives. <strong>Click each type below</strong> to
                    see what one dollar of that revenue is worth, in
                    enterprise value, on a typical lower-middle-market deal.
                </p>

                <div className="ltg-dor__grid">
                    <div className="ltg-dor__tiles">
                        {REVENUE_TYPES.map((t) => {
                            const isActive = active === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    className={`ltg-dor__tile ${isActive ? "is-active" : ""}`}
                                    onClick={() => setActive(t.key)}
                                    style={
                                        isActive
                                            ? {
                                                  borderColor: t.color,
                                                  background: t.color + "14",
                                              }
                                            : undefined
                                    }
                                    data-testid={`dor-tile-${t.key}`}
                                >
                                    <span
                                        className="ltg-dor__tile-num"
                                        style={{ color: t.color }}
                                    >
                                        ${t.multiplier.toFixed(1)}0
                                    </span>
                                    <span className="ltg-dor__tile-label">
                                        {t.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div
                        className="ltg-dor__detail"
                        style={{ borderColor: cur.color }}
                        data-testid="dor-detail"
                    >
                        <span
                            className="ltg-dor__detail-label"
                            style={{ color: cur.color }}
                        >
                            $1 of {cur.label.toLowerCase()} revenue
                        </span>
                        <div
                            className="ltg-dor__detail-big"
                            style={{ color: cur.color }}
                            data-testid="dor-detail-big"
                        >
                            = ${cur.multiplier.toFixed(2)}
                        </div>
                        <p className="ltg-dor__detail-desc">
                            <strong>What this looks like:</strong>{" "}
                            {cur.example}
                        </p>
                        <p className="ltg-dor__detail-desc">
                            <strong>Why buyers pay this:</strong> {cur.desc}{" "}
                            The market discounts uncertainty; this revenue{" "}
                            {riskCopy(cur.multiplier)} the risk of future cash
                            flows from the buyer's perspective.
                        </p>

                        <div className="ltg-dor__math">
                            <div className="ltg-dor__math-row">
                                <span className="ltg-dor__math-k">
                                    Your business adds
                                </span>
                                <span className="ltg-dor__math-v">
                                    $1.0M of this revenue annually
                                </span>
                            </div>
                            <div className="ltg-dor__math-row">
                                <span className="ltg-dor__math-k">
                                    Margin assumption
                                </span>
                                <span className="ltg-dor__math-v">
                                    15% EBITDA
                                </span>
                            </div>
                            <div className="ltg-dor__math-row">
                                <span className="ltg-dor__math-k">
                                    Sector multiple
                                </span>
                                <span className="ltg-dor__math-v">
                                    {sectorMultipleLabel(cur.multiplier)}
                                </span>
                            </div>
                            <div className="ltg-dor__math-row is-total">
                                <span className="ltg-dor__math-k">
                                    Enterprise value added
                                </span>
                                <span
                                    className="ltg-dor__math-v"
                                    style={{ color: cur.color }}
                                >
                                    ${(cur.multiplier * 1).toFixed(1)}M to $
                                    {(cur.multiplier * 1.4).toFixed(1)}M
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <p className="ltg-dor__foot">
                    That is why a Strategic Snapshot does not just compute your
                    revenue total. It maps the <em>mix</em>, and the mix is
                    often the single largest source of valuation upside an
                    owner is leaving on the table.
                </p>
            </div>
        </section>
    );
}

export default DollarOfRevenue;
